/*
  pnr-consent-box.js: the plan consent box on pnr product templates (Purenyx, local build 11 Oct 2026).
  Config: <script id="pnr-consent-config"> from sections/pnr-consent-settings.liquid. Markup: snippets/pnr-consent-box.liquid.
  What it does:
    1. Keeps the box directly above the Add to cart button (inside the product form), wherever Kaching mounts.
    2. Shows the founder-written text for the selected Kaching card (1-based, in the order shown).
       [date+N] (or [day-N date]) in a text becomes the shopper's own date + N days ("November 10, 2026"); [month+N] the same day
       N months later ("same date next month" = [month+1]). Never a cached server date.
    3. Starts unticked. While it is visible and unticked, Add to cart is blocked (capture phase, before Kaching and
       the theme) and a plain message is shown. The Subscribe / Buy once control is read inside the selected card.
    4. When ticked (and again on Add to cart), saves the shown text, version and time as cart attributes (best
       effort, never blocks the add). Optionally also as hidden line-item properties (setting, off by default).
  Kaching DOM names come from the live page on 2 Oct 2026 and can be changed in the section settings. [VERIFY on preview]
*/
(function () {
  'use strict';

  var cfgEl = document.getElementById('pnr-consent-config');
  if (!cfgEl) return;
  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  if (!cfg || cfg.enabled === false) return;

  var sel = cfg.selectors || {};
  var SEL_WIDGET = sel.widget || 'kaching-bundle';
  var SEL_RADIOS = sel.radios || 'input[type=radio][name^="kaching-bundles-deal-"]';
  var SEL_BARS = sel.bars || '.kaching-bundles__bar';
  var CLS_BAR_SELECTED = (sel.barSelected || 'kaching-bundles__bar--selected').replace(/^\./, '');
  var SEL_SUBSCRIBE = sel.subscribe || '.kaching-bundles__subscriptions';
  var PLACEHOLDER = '[Consent text for this plan — add when the offer is final]';

  var box, input, textEl, errWrap, errText, oneTimeEl, form, buttons;
  var current = { show: false, text: null, card: null };

  function q(s, root) { try { return (root || document).querySelector(s); } catch (e) { return null; } }
  function qa(s, root) { try { return Array.prototype.slice.call((root || document).querySelectorAll(s)); } catch (e) { return []; } }

  function formatDate(d) {
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
  function fillDates(t) {
    return String(t)
      .replace(/\[date\s*\+\s*(\d{1,3})\]/gi, function (m, n) {
        var d = new Date();
        d.setHours(12, 0, 0, 0);
        d.setDate(d.getDate() + parseInt(n, 10));
        return formatDate(d);
      })
      // [day-N date]: the bottle plan's master syntax (BP §2a), same as [date+N], so those texts paste in unchanged.
      .replace(/\[day-(\d{1,3}) date\]/gi, function (m, n) {
        var d = new Date();
        d.setHours(12, 0, 0, 0);
        d.setDate(d.getDate() + parseInt(n, 10));
        return formatDate(d);
      })
      // [month+N]: the same day N months from today ("same date next month" = [month+1]).
      // Jan 31 + 1 month = Feb 28 (or 29): the day is capped at the last day of the target month.
      .replace(/\[month\s*\+\s*(\d{1,2})\]/gi, function (m, n) {
        var d = new Date();
        d.setHours(12, 0, 0, 0);
        var day = d.getDate();
        d.setDate(1);
        d.setMonth(d.getMonth() + parseInt(n, 10));
        d.setDate(Math.min(day, new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()));
        return formatDate(d);
      })
      .replace(/\[today\]/gi, function () { return formatDate(new Date()); });
  }

  function buildBox() {
    var wrap = document.createElement('div');
    wrap.innerHTML =
      '<div class="pnr-consent" data-pnr-consent hidden>' +
      '<label class="pnr-consent__label" for="PnrConsent-js"><input type="checkbox" id="PnrConsent-js" class="pnr-consent__box" aria-required="true" aria-describedby="PnrConsentError-js">' +
      '<span class="pnr-consent__text" data-pnr-consent-text></span></label>' +
      '<div id="PnrConsentError-js" class="product-form__error-message-wrapper pnr-consent__error" role="alert" hidden><span data-pnr-consent-error></span></div></div>' +
      '<p class="pnr-consent__onetime" data-pnr-consent-onetime hidden></p>';
    return wrap;
  }

  function init() {
    box = q('[data-pnr-consent]');
    var sectionId = box && box.getAttribute('data-section');
    form = (sectionId && document.getElementById('product-form-' + sectionId)) ||
      q('#MainContent form[id^="product-form-"]') ||
      q('#MainContent form[action*="/cart/add"]');
    if (!form) return false;
    buttons = q('.product-form__buttons', form);
    if (!box) {
      var built = buildBox();
      (buttons ? buttons.parentNode : form).insertBefore(built.firstChild, buttons || null);
      (buttons ? buttons.parentNode : form).insertBefore(built.firstChild, buttons || null);
      box = q('[data-pnr-consent]');
    }
    input = q('.pnr-consent__box', box);
    textEl = q('[data-pnr-consent-text]', box);
    errWrap = q('.pnr-consent__error', box);
    errText = q('[data-pnr-consent-error]', box);
    oneTimeEl = box.nextElementSibling && box.nextElementSibling.hasAttribute('data-pnr-consent-onetime') ? box.nextElementSibling : q('[data-pnr-consent-onetime]');
    place();
    input.addEventListener('change', function () {
      box.classList.toggle('pnr-consent--checked', input.checked);
      if (input.checked) {
        hideError();
        // Save the record as soon as the box is ticked, so the cart (and its cookie) exists before Add to cart
        // is tapped. A first-time visitor has no cart yet: posting in parallel with the add could split carts.
        saveConsent();
      }
      syncLineProps();
    });
    return true;
  }

  // 1. Directly above the Add to cart button, inside the form (no name on the checkbox, so nothing is posted).
  function place() {
    if (!buttons || !box) return;
    var parent = buttons.parentNode;
    if (box.nextElementSibling === oneTimeEl && oneTimeEl && oneTimeEl.nextElementSibling === buttons) return;
    parent.insertBefore(box, buttons);
    if (oneTimeEl) parent.insertBefore(oneTimeEl, buttons);
  }

  function widget() { return q(SEL_WIDGET) || document; }

  // 2. Which card is selected (1-based). 0 = no Kaching cards found.
  function selectedCard() {
    var root = widget();
    var radios = qa(SEL_RADIOS, root);
    if (radios.length) {
      var name = radios[0].name;
      radios = radios.filter(function (r) { return r.name === name; });
      for (var i = 0; i < radios.length; i++) if (radios[i].checked) return i + 1;
    }
    var bars = qa(SEL_BARS, root);
    for (var j = 0; j < bars.length; j++) if (bars[j].classList.contains(CLS_BAR_SELECTED)) return j + 1;
    return 0;
  }

  // The selected card element (null if none can be found).
  function selectedBar(root) {
    var bar = q('.' + CLS_BAR_SELECTED, root);
    if (bar) return bar;
    var radios = qa(SEL_RADIOS, root);
    for (var i = 0; i < radios.length; i++) {
      if (!radios[i].checked) continue;
      try { bar = radios[i].closest(SEL_BARS); } catch (e) { bar = null; }
      if (bar) return bar;
    }
    return null;
  }

  // One-time choice is only assumed when a control says so. If we can't tell, the box is required (fail safe).
  // Kaching can draw one Subscribe / Buy once control for the whole deal, or one inside each card. Read the
  // control inside the SELECTED card first; use a deal-wide control only if it sits outside every card.
  function oneTimeChosen() {
    var root = widget();
    var selBar = selectedBar(root);
    var sub = selBar ? q(SEL_SUBSCRIBE, selBar) : null;
    if (!sub) {
      var any = q(SEL_SUBSCRIBE, root);
      var inBar = null;
      try { inBar = any && any.closest(SEL_BARS); } catch (e) { inBar = null; }
      if (any && !inBar) sub = any;
    }
    if (!sub) return false;
    var cb = q('input[type=checkbox]', sub);
    if (cb) return !cb.checked;
    var aria = q('[aria-checked]', sub) || (sub.hasAttribute('aria-checked') ? sub : null);
    if (aria) return aria.getAttribute('aria-checked') === 'false';
    var on = sub.matches('[class*="--selected"],[class*="--active"],[class*="--checked"]') ||
      !!q('[class*="--selected"],[class*="--active"],[class*="--checked"]', sub);
    return !on;
  }

  function textFor(card) {
    var texts = cfg.texts || [];
    var t = card > 0 ? texts[card - 1] : '';
    if (!t || !String(t).trim()) t = cfg.defaultText || PLACEHOLDER;
    return String(t).trim();
  }

  function render() {
    if (!box) return;
    place();
    var card = selectedCard();
    var raw = textFor(card);
    var none = /^(none|-)$/i.test(raw);
    var oneTime = none || (cfg.mode !== 'always' && oneTimeChosen());
    var show = !oneTime;
    var text = show ? fillDates(raw) : '';
    if (show && (text !== current.text || card !== current.card || !current.show)) {
      // Other terms on screen (new text, another card, or back from one-time): the shopper must tick again.
      // The card is tracked too: two cards can share the default text but not the same price (L2 review, 11 Oct).
      if (textEl) textEl.textContent = text;
      if (input.checked) input.checked = false;
      if (box.classList.contains('pnr-consent--checked')) box.classList.remove('pnr-consent--checked');
    }
    current.show = show;
    current.text = text;
    current.card = card;
    if (box.hidden !== !show) box.hidden = !show;
    if (!show) hideError();
    if (oneTimeEl) {
      var line = cfg.oneTimeText || '';
      if (oneTimeEl.textContent !== line) oneTimeEl.textContent = line;
      var hideLine = show || !line;
      if (oneTimeEl.hidden !== hideLine) oneTimeEl.hidden = hideLine;
    }
    syncLineProps();
  }

  function showError() {
    if (!errWrap) return;
    errText.textContent = cfg.errorText || 'Please tick the box to start your plan.';
    errWrap.hidden = false;
    box.classList.add('pnr-consent--error');
    try { box.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { box.scrollIntoView(); }
    try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
  }
  function hideError() {
    if (errWrap && !errWrap.hidden) errWrap.hidden = true;
    if (box && box.classList.contains('pnr-consent--error')) box.classList.remove('pnr-consent--error');
  }

  function saveConsent() {
    if (!cfg.saveToCart || !window.fetch) return;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    try {
      fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        keepalive: true,
        body: JSON.stringify({ attributes: {
          'Plan consent text': current.text,
          'Plan consent version': cfg.version || '',
          'Plan consent time': new Date().toISOString(),
          'Plan consent page': cfg.template || ''
        } })
      }).catch(function () {});
    } catch (e) {}
  }

  // Optional (setting "Also save on the order line", off by default): hidden line-item properties in the
  // product form while the box is ticked. A leading "_" hides them at checkout; they stay on the order line.
  // [VERIFY on preview] that Kaching passes form properties through when it adds its deal.
  var PROP_TEXT = 'properties[_Plan consent]';
  var PROP_VERSION = 'properties[_Plan consent version]';
  function syncLineProps() {
    if (!cfg.lineProperty || !form || !box || !input) return;
    var want = !box.hidden && input.checked && current.text;
    [[PROP_TEXT, current.text], [PROP_VERSION, cfg.version || '']].forEach(function (p) {
      var el = null;
      qa('input[type=hidden][data-pnr-consent-prop]', form).forEach(function (x) { if (x.name === p[0]) el = x; });
      if (!want) { if (el) el.parentNode.removeChild(el); return; }
      if (!el) {
        el = document.createElement('input');
        el.type = 'hidden';
        el.name = p[0];
        el.setAttribute('data-pnr-consent-prop', '');
        form.appendChild(el);
      }
      if (el.value !== p[1]) el.value = p[1];
    });
  }

  // 3. Gate. Capture phase on document runs before Kaching's and the theme's own handlers.
  // Only the Add to cart button(s) in the buy box: never Kaching's own card controls.
  function isAddTrigger(t) {
    if (!t || !t.closest || !form) return false;
    var btn = t.closest('button, input[type=submit]');
    if (!btn || !form.contains(btn)) return false;
    return !!(btn.closest('.product-form__buttons') || /^ProductSubmitButton-/.test(btn.id || ''));
  }
  function gate(e) {
    if (!box || box.hidden) return;
    var hit = e.type === 'submit' ? e.target === form : isAddTrigger(e.target);
    if (!hit) return;
    if (!input.checked) {
      e.preventDefault();
      e.stopImmediatePropagation();
      showError();
      return;
    }
    if (e.type === 'click') saveConsent();
  }

  function start() {
    if (!init()) return;
    render();
    window.addEventListener('click', gate, true);
    window.addEventListener('submit', gate, true);
    // Kaching renders after load and re-renders on every choice: watch the buy box.
    var watchRoot = form.closest('.product__info-container') || form.closest('section') || document.body;
    var t = null;
    var mo = new MutationObserver(function (records) {
      var outside = records.some(function (r) {
        return !(box.contains(r.target) || (oneTimeEl && oneTimeEl.contains(r.target)));
      });
      if (!outside || t) return;
      t = setTimeout(function () { t = null; render(); }, 60);
    });
    mo.observe(watchRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'checked', 'aria-checked'] });
    document.addEventListener('change', function (e) {
      if (e.target && e.target !== input) setTimeout(render, 0);
    });
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest(SEL_WIDGET)) setTimeout(render, 30);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
